import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ImageProxyPipe, ImageProxyUrlOptions } from '../pipes/image-proxy.pipe';
import { ImageProxySrcSetOptions, ImageProxySrcSetPipe } from '../pipes/image-proxy-src-set.pipe';

@Component({
  selector: 'nist-img',
  standalone: true,
  imports: [NgOptimizedImage, ImageProxyPipe, ImageProxySrcSetPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    style: 'display: contents'
  },
  template: `
    @if (usePicture()) {
      <picture>
        @if (enableAvif()) {
          <source
            type="image/avif"
            [attr.srcset]="filename() | imageProxySrcSet : avifSrcSetOptions()"
          />
        }

        @if (enableWebpSource()) {
          <source
            type="image/webp"
            [attr.srcset]="filename() | imageProxySrcSet : webpSrcSetOptions()"
          />
        }

        <img
          [class]="classes()"
          [ngSrc]="filename() | imageProxy : fallbackSrcOptions()"
          [attr.srcset]="fallbackImgSrcSetEnabled() ? (filename() | imageProxySrcSet : fallbackSrcSetOptions()) : null"
          [alt]="alt()"
          [width]="width()"
          [height]="height()"
          [attr.loading]="effectiveLoading()"
          [decoding]="decoding()"
          [priority]="priority()"
        />
      </picture>
    } @else {
      <img
        [class]="classes()"
        [ngSrc]="filename() | imageProxy : imgSrcOptions()"
        [attr.srcset]="imgSrcSetEnabled() ? (filename() | imageProxySrcSet : imgSrcSetOptions()) : null"
        [alt]="alt()"
        [width]="width()"
        [height]="height()"
        [attr.loading]="effectiveLoading()"
        [decoding]="decoding()"
        [priority]="priority()"
      />
    }
  `
})
export class NistImgComponent {
  readonly filename = input.required<string>();
  readonly alt = input<string>('');
  readonly width = input.required<number>();
  readonly height = input.required<number>();

  readonly classes = input<string>('');

  readonly mode = input<'auto' | 'img' | 'picture'>('auto');

  readonly format = input<'auto' | 'avif' | 'webp' | 'png' | 'jpg' | 'jpeg'>('auto');
  readonly fallbackFormat = input<'webp' | 'png' | 'jpg' | 'jpeg'>('webp');
  readonly quality = input<number | undefined>(undefined);

  readonly densities = input<readonly number[]>([1, 2]);

  readonly loading = input<'eager' | 'lazy'>('lazy');
  readonly decoding = input<'sync' | 'async' | 'auto'>('async');
  readonly priority = input<boolean>(false);

  readonly isSvg = computed(() =>
    this.filename().trim().toLowerCase().endsWith('.svg')
  );

  readonly usePicture = computed(() => {
    const mode = this.mode();

    if (mode === 'img') {
      return false;
    }

    if (mode === 'picture') {
      return !this.isSvg();
    }

    // auto
    return !this.isSvg() && this.format() === 'avif';
  });

  readonly imgEffectiveFormat = computed(() => {
    if (this.isSvg()) {
      return undefined;
    }

    return this.usePicture() ? this.fallbackFormat() : this.format();
  });

  readonly imgSrcSetEnabled = computed(() => !this.isSvg());

  readonly fallbackImgSrcSetEnabled = computed(() => true);

  readonly enableAvif = computed(() => !this.isSvg());

  readonly enableWebpSource = computed(() =>
    !this.isSvg() && this.fallbackFormat() !== 'webp'
  );

  readonly imgSrcOptions = computed<ImageProxyUrlOptions>(() => ({
    width: this.width(),
    height: this.height(),
    format: this.imgEffectiveFormat(),
    quality: this.quality()
  }));

  readonly imgSrcSetOptions = computed<ImageProxySrcSetOptions>(() => ({
    width: this.width(),
    height: this.height(),
    format: this.imgEffectiveFormat(),
    quality: this.quality(),
    densities: this.densities()
  }));

  readonly avifSrcSetOptions = computed<ImageProxySrcSetOptions>(() => ({
    width: this.width(),
    height: this.height(),
    format: 'avif',
    quality: this.quality(),
    densities: this.densities()
  }));

  readonly webpSrcSetOptions = computed<ImageProxySrcSetOptions>(() => ({
    width: this.width(),
    height: this.height(),
    format: 'webp',
    quality: this.quality(),
    densities: this.densities()
  }));

  readonly fallbackSrcOptions = computed<ImageProxyUrlOptions>(() => ({
    width: this.width(),
    height: this.height(),
    format: this.fallbackFormat(),
    quality: this.quality()
  }));

  readonly fallbackSrcSetOptions = computed<ImageProxySrcSetOptions>(() => ({
    width: this.width(),
    height: this.height(),
    format: this.fallbackFormat(),
    quality: this.quality(),
    densities: this.densities()
  }));

  readonly effectiveLoading = computed<'eager' | 'lazy' | null>(() =>
    this.priority() ? null : this.loading()
  );
}
